import React from "react";
import { Input, Typography, Alert } from "antd";

const { Text } = Typography;

/**
 * Interprète une saisie de numéros d'attestations sous différents formats :
 *   - Plages avec tirets        : "1001-1050"
 *   - Numéros séparés par virgule: "1001,1005,1010"
 *   - Combinaison des deux       : "1001-1010,1020,1030-1040"
 *
 * L'ordre de saisie est conservé tel quel (aucun tri) : c'est cet ordre qui
 * correspond au rangement physique des attestations dans l'imprimante. Les
 * doublons sont retirés en gardant la première occurrence.
 *
 * @returns {{ numeros: number[], invalid: string[] }}
 */
export const parseAttestationNumbers = (input) => {
    if (!input || !input.trim()) {
        return { numeros: [], invalid: [] };
    }

    const tokens = input
        .split(",")
        .map((token) => token.trim())
        .filter(Boolean);

    const numeros = [];
    const seen = new Set();
    const invalid = [];

    const pushUnique = (n) => {
        if (!seen.has(n)) {
            seen.add(n);
            numeros.push(n);
        }
    };

    const isPositiveInteger = (raw) => /^\d+$/.test(raw);

    tokens.forEach((token) => {
        const bounds = token.split("-").map((b) => b.trim());

        if (bounds.length === 1) {
            // Numéro unitaire
            if (isPositiveInteger(bounds[0])) {
                pushUnique(parseInt(bounds[0], 10));
            } else {
                invalid.push(token);
            }
        } else if (bounds.length === 2 && isPositiveInteger(bounds[0]) && isPositiveInteger(bounds[1])) {
            // Plage : on respecte le sens de saisie (croissant ou décroissant)
            const start = parseInt(bounds[0], 10);
            const end = parseInt(bounds[1], 10);

            if (start <= end) {
                for (let i = start; i <= end; i++) pushUnique(i);
            } else {
                for (let i = start; i >= end; i--) pushUnique(i);
            }
        } else {
            invalid.push(token);
        }
    });

    return { numeros, invalid };
};

/**
 * Champ de saisie (contrôlé) des numéros d'attestations.
 *
 * Les numéros saisis sont distribués, dans leur ordre de saisie, à chaque
 * attestation/engin : le 1er numéro à la 1ère attestation, etc. Ils servent au
 * contrôle visuel de l'ordre et ne sont pas imprimés sur les attestations.
 */
const AttestationsNumerosInput = ({ value, onChange, numeros = [], invalid = [], enginsCount }) => {
    const countMismatch =
        typeof enginsCount === "number" &&
        enginsCount > 0 &&
        numeros.length > 0 &&
        numeros.length !== enginsCount;

    return (
        <div
            style={{
                marginBottom: 24,
                padding: 16,
                border: "1px solid #f0f0f0",
                borderRadius: 8,
                background: "#fafafa",
            }}
        >
            <Text strong>Saisir les numéros d'attestations</Text>
            <Input
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder="ex : 1001-1010,1020,1030-1040"
                allowClear
                style={{ marginTop: 8 }}
            />
            <Text type="secondary" style={{ display: "block", marginTop: 6, fontSize: 12 }}>
                Formats acceptés : plages (1001-1050), numéros séparés par des virgules
                (1001,1005,1010) ou combinaison (1001-1010,1020,1030-1040). Les numéros sont
                attribués dans l'ordre de saisie, attestation après attestation.
            </Text>

            {invalid.length > 0 && (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginTop: 12 }}
                    message="Segments ignorés"
                    description={`Format non reconnu : ${invalid.join(", ")}`}
                />
            )}

            {countMismatch && (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginTop: 12 }}
                    message="Nombre de numéros différent du nombre d'engins"
                    description={`${numeros.length} numéro(s) saisi(s) pour ${enginsCount} engin(s).`}
                />
            )}
        </div>
    );
};

export default AttestationsNumerosInput;
